// subscriptions.js — recurring-charge (subscription) detection and auto-paid
// matching (2026-10-20). The outflow mirror of the confirmed-income flow:
// detect → ask once per merchant (recurring_rules) → the user adds it to bills
// or says it is not a bill. Nothing is ever added silently.
//
// Pure ESM, shared by the client (Finance card, auto-paid on load) and the
// server (api/lib/plaid-sync.js auto-paid after a sync). Unit tested.
import { isExcludedFromSpend, spendCategory, spendBucket, txLocalDay } from './spend.js';
import { normalizeInflowName } from './income.js';
import { yearlyDueDate } from './bills.js';

export const AMOUNT_TOLERANCE = 0.10;   // amounts within ~10% of the typical charge
export const MIN_OCCURRENCES = 3;
export const PAID_WINDOW_DAYS = 5;     // a charge within ±5 days of the due day pays the bill

const DAY = 86400000;
const dayMs = (d) => Date.parse(`${d}T12:00:00Z`);
const median = (xs) => { const s = [...xs].sort((a, b) => a - b); const m = Math.floor(s.length / 2); return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };
const within = (a, typical) => typical > 0 && Math.abs(a - typical) <= typical * AMOUNT_TOLERANCE;

/** The merchant key: merchant_name (else name), normalized like income names. */
export const merchantKey = (t) => normalizeInflowName(t?.merchant_name || t?.name || '');

/** Is a bill already this merchant? Its stored match_name, else its own name normalized. */
export const billMatchesKey = (bill, key) => !!key && (bill?.match_name === key || normalizeInflowName(bill?.name || '') === key);

// A charge that can be a subscription: a posted outflow that counts as spend,
// never a transfer (to people or own accounts, ATM cash) or a loan/card
// payment — and never interest, bank/ATM fees or gas (2026-10-20: a card's
// monthly interest and a gas station read as "subscriptions").
const NOT_A_SUBSCRIPTION_PRIMARY = new Set(['TRANSFER_OUT', 'TRANSFER_IN', 'BANK_FEES']);
const NOT_A_SUBSCRIPTION_DETAILED = /^(TRANSPORTATION_GAS|TRANSFER_OUT_WITHDRAWAL|BANK_FEES_)/;
const NOT_A_SUBSCRIPTION_NAME = /(?:^|[^A-Z])(?:INTEREST CHARGE|ATM|ATM WITHDRAWAL|OVERDRAFT|SERVICE FEE|MONTHLY FEE|LATE FEE)(?=$|[^A-Z])/;
export const isExcludedCharge = (t) => NOT_A_SUBSCRIPTION_PRIMARY.has(spendCategory(t))
  || NOT_A_SUBSCRIPTION_DETAILED.test(t?.personal_finance_category?.detailed || '')
  || NOT_A_SUBSCRIPTION_NAME.test(`${t?.merchant_name || ''} ${t?.name || ''}`.toUpperCase());
const isChargeCandidate = (t) => t && !t.pending && Number(t.amount) > 0 && !isExcludedFromSpend(t) && !isExcludedCharge(t);

/**
 * Recurring patterns in synced transactions: same merchant, amount within 10%
 * of its typical charge, a monthly (25-35 day gaps) or weekly (6-8 day gaps)
 * run of 3+ ending at the most recent charge.
 * @returns [{ matchName, displayName, cadence, typicalAmount, usualDay, weekday, count, lastDate, monthlyAmount }]
 */
export function detectRecurringCharges(transactions = [], { zone } = {}) {
  const groups = new Map();
  for (const t of transactions || []) {
    if (!isChargeCandidate(t)) continue;
    const key = merchantKey(t);
    const d = txLocalDay(t, zone);
    if (!key || !d) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ d, amount: Number(t.amount), name: t.merchant_name || t.name || key, bucket: spendBucket(t), detailed: t?.personal_finance_category?.detailed || '' });
  }
  const out = [];
  for (const [key, rows] of groups) {
    if (rows.length < MIN_OCCURRENCES) continue;
    const typical0 = median(rows.map(r => r.amount));
    const kept = rows.filter(r => within(r.amount, typical0)).sort((a, b) => a.d.localeCompare(b.d));
    // One charge per day (a duplicate posting is not a second cycle).
    const byDay = []; for (const r of kept) if (!byDay.length || byDay[byDay.length - 1].d !== r.d) byDay.push(r);
    if (byDay.length < MIN_OCCURRENCES) continue;
    for (const [cadence, lo, hi] of [['monthly', 25, 35], ['weekly', 6, 8]]) {
      // The trailing run that fits this cadence, newest backwards.
      const run = [byDay[byDay.length - 1]];
      for (let i = byDay.length - 2; i >= 0; i--) {
        const gap = Math.round((dayMs(run[0].d) - dayMs(byDay[i].d)) / DAY);
        if (gap < lo) continue;          // too close: skip it (not a new cycle)
        if (gap > hi) break;             // the run is broken
        run.unshift(byDay[i]);
      }
      if (run.length < MIN_OCCURRENCES) continue;
      const typicalAmount = Math.round(median(run.map(r => r.amount)) * 100) / 100;
      const usualDay = Math.round(median(run.map(r => Number(r.d.slice(8, 10)))));
      const weekday = new Date(dayMs(run[run.length - 1].d)).getUTCDay();
      out.push({
        matchName: key, displayName: run[run.length - 1].name, cadence, typicalAmount,
        // 'bill' = rent / utilities / insurance (spendBucket): still offered as a
        // bill, but not counted in the Subscriptions total.
        bucket: run[run.length - 1].bucket,
        detailed: run[run.length - 1].detailed,
        usualDay: Math.min(usualDay, 28), weekday, count: run.length, lastDate: run[run.length - 1].d,
        monthlyAmount: Math.round((cadence === 'weekly' ? typicalAmount * 52 / 12 : typicalAmount) * 100) / 100,
      });
      break; // monthly wins over weekly for the same merchant
    }
  }
  return out.sort((a, b) => b.monthlyAmount - a.monthlyAmount);
}

/**
 * The Finance card's questions: detected patterns with no recurring_rules
 * answer yet and not already a bill. Asked once per merchant, ever.
 */
export function recurringCandidates(detected = [], { rules = [], bills = [] } = {}) {
  const answered = new Set((rules || []).map(r => r.match_name));
  // Subscriptions (everyday bucket) first, then rent/utilities; larger first within each.
  return (detected || []).filter(p => !answered.has(p.matchName) && !(bills || []).some(b => billMatchesKey(b, p.matchName)))
    .sort((a, b) => (a.bucket === 'everyday' ? 0 : 1) - (b.bucket === 'everyday' ? 0 : 1) || b.monthlyAmount - a.monthlyAmount);
}

// ── CONFIRMED SUBSCRIPTIONS (2026-10-20) ──────────────────────────────────
// A confirmed subscription IS a bill with category 'subscriptions' (from a
// detected pattern or "+ Add subscription"), so it rides the bill calendar,
// Up next and every bill reader. Weekly ones carry cadence 'weekly' + weekday.
export const isSubscriptionBill = (b) => b?.category === 'subscriptions';
export const monthlyEquivalent = (b) => Math.round((Number(b?.amount) || 0) * (b?.cadence === 'weekly' ? 52 / 12 : b?.cadence === 'yearly' ? 1 / 12 : 1) * 100) / 100;

/**
 * "Subscriptions" monthly total: every confirmed subscription bill (at its
 * monthly equivalent, with the user's edited amount), plus detected
 * everyday-bucket patterns still unanswered (not a bill yet, no rule) — never
 * rent, utilities, insurance, or anything answered "not a bill".
 */
export function subscriptionsMonthlyTotal({ bills = [], detected = [], rules = [] } = {}) {
  const subs = (bills || []).filter(isSubscriptionBill);
  const pending = recurringCandidates(detected, { rules, bills }).filter(p => p.bucket === 'everyday');
  return Math.round((subs.reduce((a, b) => a + monthlyEquivalent(b), 0) + pending.reduce((a, p) => a + p.monthlyAmount, 0)) * 100) / 100;
}

// A detected bill-bucket pattern's bill category (rent → housing, insurance →
// insurance, other obligations → utilities); everyday patterns are subscriptions.
const billCategoryFor = (p) => p.bucket !== 'bill' ? 'subscriptions'
  : /RENT_AND_UTILITIES_RENT/.test(p.detailed || '') ? 'housing'
  : /INSURANCE/.test(p.detailed || '') ? 'insurance' : 'utilities';

/**
 * The bill row a detected pattern becomes ("Add to bills" / "Yes, it's
 * recurring"). Subscriptions charge themselves, so they are autopay (no "go
 * pay this" reminders); rent and utilities keep the user's normal bill flow.
 * Weekly patterns become weekly bills on their weekday.
 */
export function billFromPattern(p, userId) {
  const category = billCategoryFor(p);
  const weekly = p.cadence === 'weekly';
  return { user_id: userId, name: p.displayName, amount: p.typicalAmount, due_day: weekly ? null : p.usualDay,
    status: 'upcoming', category, autopay: category === 'subscriptions', match_name: p.matchName,
    cadence: weekly ? 'weekly' : 'monthly', weekday: weekly ? p.weekday : null };
}

/**
 * "+ Add subscription": name, amount, day (monthly: day of month; weekly:
 * weekday 0=Sunday), cadence, and an OPTIONAL merchant picked from recent
 * transactions. Without a picked merchant match_name stays null, so it never
 * auto-marks paid.
 */
export function billFromManual({ name, amount, day, cadence = 'monthly', month = null, matchName = null }, userId) {
  const weekly = cadence === 'weekly', yearly = cadence === 'yearly';
  const d = parseInt(day, 10), mo = parseInt(month, 10);
  return { user_id: userId, name: String(name || '').trim(), amount: Math.round((parseFloat(amount) || 0) * 100) / 100,
    due_day: weekly ? null : Math.min(Math.max(Number.isFinite(d) ? d : 1, 1), 31), status: 'upcoming', category: 'subscriptions',
    autopay: true, match_name: matchName || null, cadence: weekly ? 'weekly' : yearly ? 'yearly' : 'monthly',
    weekday: weekly ? Math.min(Math.max(Number.isFinite(d) ? d : 0, 0), 6) : null,
    // Yearly (2026-10-20): due on due_month + due_day.
    due_month: yearly ? Math.min(Math.max(Number.isFinite(mo) ? mo : 1, 1), 12) : null };
}

/** The ask-once key for a bill being removed as "Not a subscription". */
export const ruleKeyForBill = (b) => b?.match_name || normalizeInflowName(b?.name || '');

/**
 * Merchants from recent charges (newest first, one per merchant), for the
 * manual add's "match a merchant" picker.
 */
export function recentMerchants(transactions = [], { zone, today, days = 60 } = {}) {
  const cutoff = today ? new Date(dayMs(today) - days * DAY).toISOString().slice(0, 10) : '0000-00-00';
  const seen = new Map();
  const rows = (transactions || []).filter(isChargeCandidate).map(t => ({ t, d: txLocalDay(t, zone) })).filter(x => x.d && x.d >= cutoff)
    .sort((a, b) => b.d.localeCompare(a.d));
  for (const { t, d } of rows) {
    const key = merchantKey(t);
    if (key && !seen.has(key)) seen.set(key, { matchName: key, displayName: t.merchant_name || t.name || key, lastAmount: Number(t.amount), lastDate: d });
  }
  return [...seen.values()];
}

/**
 * One list row: next charge date and this month's paid state. Weekly rows
 * have no monthly paid state (paidThisMonth null).
 */
export function subscriptionRow(b, { today, month } = {}) {
  const [y, m, d] = String(today).split('-').map(Number);
  let days = null;
  if (b?.cadence === 'yearly') {
    const t = Date.UTC(y, m - 1, d);
    for (const yy of [y, y + 1]) { const due = yearlyDueDate(b, yy); if (due && Date.parse(`${due}T00:00:00Z`) >= t) { days = Math.round((Date.parse(`${due}T00:00:00Z`) - t) / DAY); break; } }
  } else if (b?.cadence === 'weekly') {
    const wd = Number(b.weekday);
    if (Number.isInteger(wd)) days = (wd - new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay() + 7) % 7;
  } else {
    const day = Number(b?.due_day);
    if (Number.isInteger(day) && day >= 1) {
      const dim = (yy, mm) => new Date(Date.UTC(yy, mm, 0)).getUTCDate();
      const thisDue = Math.min(day, dim(y, m));
      if (thisDue >= d) days = thisDue - d;
      else { const ny = m === 12 ? y + 1 : y, nm = m === 12 ? 1 : m + 1; days = dim(y, m) - d + Math.min(day, dim(ny, nm)); }
    }
  }
  const nextDate = days == null ? null : new Date(Date.UTC(y, m - 1, d + days, 12)).toISOString().slice(0, 10);
  // Yearly: paid when stamped for this year's due month (paidThisYear); weekly: no paid state.
  const thisYearDue = b?.cadence === 'yearly' ? (yearlyDueDate(b, y) || '').slice(0, 7) : null;
  return { nextDate, paidThisMonth: b?.cadence === 'weekly' || b?.cadence === 'yearly' ? null : b?.paid_for_month === month,
    paidThisYear: b?.cadence === 'yearly' ? (!!thisYearDue && b?.paid_for_month === thisYearDue) : null, monthlyAmount: monthlyEquivalent(b) };
}

/**
 * The charge that pays `bill` for `month` ('YYYY-MM'): same merchant
 * (bill.match_name), amount within 10% of the bill, local day within ±5 days
 * of the due day, in that month. Null when none — or when the bill has no
 * match_name (only bills added from a detected pattern auto-mark).
 */
export const YEARLY_PAID_WINDOW_DAYS = 7;
export function findPaymentCharge(bill, transactions = [], { month, zone } = {}) {
  if (!bill?.match_name || !month) return null;
  const [y, m] = month.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  // Yearly (2026-10-20): `month` is the due month; a charge within ±7 days of
  // the due date counts even across the month boundary.
  const yearly = bill.cadence === 'yearly';
  const window = yearly ? YEARLY_PAID_WINDOW_DAYS : PAID_WINDOW_DAYS;
  const due = `${month}-${String(Math.min(Math.max(1, Number(bill.due_day) || 1), last)).padStart(2, '0')}`;
  const amt = Number(bill.amount) || 0;
  // A per-amount key ("APPLE|1.99", one App Store subscription) matches that
  // merchant's charge at that amount (within 5%).
  const { base, amount: keyAmount } = splitKey(bill.match_name);
  for (const t of transactions || []) {
    if (!isChargeCandidate(t) || merchantKey(t) !== base) continue;
    if (keyAmount != null && !nearAmount(Number(t.amount), keyAmount, POSSIBLE_AMOUNT_TOLERANCE)) continue;
    const d = txLocalDay(t, zone);
    if (!d || (!yearly && !d.startsWith(month))) continue;
    if (Math.abs(dayMs(d) - dayMs(due)) > window * DAY) continue;
    if (amt > 0 && !within(Number(t.amount), amt)) continue;
    return { date: d, amount: Number(t.amount) };
  }
  return null;
}

/**
 * Bills to stamp paid now, each with `paidStamp` (the paid_for_month to write):
 * monthly → `month`; yearly → this year's due month ('YYYY-MM'), matched
 * within ±7 days of its due date. Weekly bills have no paid state.
 */
export function billsToAutoMarkPaid(bills = [], transactions = [], { month, zone } = {}) {
  const out = [];
  for (const b of bills || []) {
    if (!b || !b.match_name || b.cadence === 'weekly') continue;
    const stamp = b.cadence === 'yearly' ? (yearlyDueDate(b, Number(String(month).slice(0, 4))) || '').slice(0, 7) : month;
    if (!stamp || b.paid_for_month === stamp) continue;
    if (findPaymentCharge(b, transactions, { month: stamp, zone })) out.push({ ...b, paidStamp: stamp });
  }
  return out;
}

/** 1 → "1st", 22 → "22nd" (the card's "usually around the 15th"). */
export function ordinal(n) {
  const v = Number(n) || 0, t = v % 100;
  return `${v}${t >= 11 && t <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[v % 10] || 'th')}`;
}

// ── POSSIBLE SUBSCRIPTIONS (2026-10-20, tightened) ──────────────────────────
// Shown below the confirmed list with a plain "Add". A merchant qualifies when
//   KNOWN: a charge in the last 45 days from the known-subscription list
//     (matched narrowly: word-bounded on Plaid's merchant_name, else the bank
//     name); OR
//   APP STORE: Apple-billed charges ("APPLE.COM/BILL"), one group per amount,
//     the same amount 25-35 days apart → "App Store subscription"; OR
//   RECURRING: a charge the bank labels "RECURRING PAYMENT" (or Plaid flags
//     recurring) in the last 45 days, from its first charge — Apple billing
//     included, per amount; OR
//   PATTERN: ALL of — (a) at most one charge per cycle (no other charge from
//     that merchant in between), (b) amounts within ~5%, (c) 2+ cycles
//     25-35 days apart, (d) not food & drink, groceries, general
//     merchandise, transportation/rideshare or delivery, (e) not a transfer
//     or a payment to a person.
// Never confirmed, answered, strict-detected, transfers or rent/utilities.
export const POSSIBLE_AMOUNT_TOLERANCE = 0.05;
export const KNOWN_MERCHANT_WINDOW_DAYS = 45;
export const APP_STORE_LABEL = 'App Store subscription';

const W = (s) => new RegExp(`(?:^|[^A-Z0-9])(?:${s})(?=$|[^A-Z0-9])`);
export const KNOWN_SUBSCRIPTION_MERCHANTS = [
  // streaming
  ['Crunchyroll', W('CRUNCHYROLL')], ['Netflix', W('NETFLIX')], ['Hulu', W('HULU')], ['Max', W('HBO MAX|HBOMAX|MAX\\.COM|HBO')],
  ['Disney+', W('DISNEY PLUS|DISNEYPLUS|DISNEY\\+')], ['Paramount+', W('PARAMOUNT PLUS|PARAMOUNT\\+|PARAMOUNTPLUS')], ['Peacock', W('PEACOCK(?:TV)?')],
  ['Apple TV', W('APPLE TV\\+?')], ['YouTube Premium', W('YOUTUBE PREMIUM|YOUTUBEPREMIUM|YOUTUBE TV')],
  // Amazon sells rentals and goods under these names: mixed.
  ['Prime Video', W('PRIME VIDEO|AMAZON PRIME|AMZN PRIME|PRIME MEMBERSHIP'), 'mixed'],
  // music
  ['Spotify', W('SPOTIFY')], ['Apple Music', W('APPLE MUSIC')], ['Tidal', W('TIDAL')], ['SoundCloud', W('SOUNDCLOUD')],
  // cloud / apps
  ['iCloud', W('ICLOUD')], ['Google One', W('GOOGLE ONE|GOOGLE \\*?STORAGE|GOOGLE \\*?GOOGLE ONE')], ['Dropbox', W('DROPBOX')],
  ['Microsoft 365', W('MICROSOFT 365|MICROSOFT\\*?365|MSFT \\*?365|OFFICE 365'), 'mixed'], ['Adobe', W('ADOBE')], ['Canva', W('CANVA')], ['Notion', W('NOTION LABS|NOTION\\.SO|NOTION')],
  ['Google', W('GOOGLE \\*?YOUTUBE|GOOGLE \\*?PLAY|GOOGLE \\*'), 'mixed'],
  // AI
  ['Claude', W('ANTHROPIC|CLAUDE\\.AI|CLAUDE AI')], ['ChatGPT', W('OPENAI|CHATGPT')], ['Midjourney', W('MIDJOURNEY')], ['Perplexity', W('PERPLEXITY')],
  // gaming
  // Game stores sell one-off games too: mixed.
  ['Xbox', W('XBOX|XBOX GAME PASS'), 'mixed'], ['PlayStation', W('PLAYSTATION|PLAYSTATION NETWORK|SONY INTERACTIVE'), 'mixed'], ['Nintendo', W('NINTENDO'), 'mixed'],
  // news
  ['New York Times', W('NYTIMES|NEW YORK TIMES|NYT')], ['Wall Street Journal', W('WSJ|WALL STREET JOURNAL|DOW JONES')], ['Washington Post', W('WASHINGTON POST|WASHPOST')],
  ['The Atlantic', W('THE ATLANTIC')], ['The Economist', W('ECONOMIST')],
  // gyms and fitness apps
  // Gyms sell snacks, drinks and gear at the front desk: mixed.
  ['Gym', W('PLANET FITNESS|EQUINOX|24 HOUR FITNESS|LA FITNESS|CRUNCH FITNESS|ORANGETHEORY|ANYTIME FITNESS|IN-SHAPE|IN SHAPE|YMCA|GOLDS GYM|GOLD\'S GYM|LIFE TIME FITNESS|LIFETIME FITNESS|COREPOWER|SOULCYCLE|CLASSPASS|F45|BARRY\'S'), 'mixed'],
  ['Peloton', W('PELOTON'), 'mixed'],
  ['Fitness app', W('STRAVA|FITBOD|NIKE TRAINING|ZWIFT|WHOOP|OURA')],
];
// Two kinds (2026-10-20): SUBSCRIPTION-ONLY merchants (streaming, music, AI,
// cloud storage, news, single-purpose apps) qualify from one charge; MIXED
// merchants (gyms, Amazon, Apple, Google, Microsoft, game stores, Peloton)
// also sell one-off things, so they need 2+ charges at the same amount
// (within 5%) 25-35 days apart, like the pattern rule.
/** { label, mixed } for a known merchant (merchant_name first, else the bank name), or null. */
export function knownSubscriptionInfo(t) {
  for (const raw of [t?.merchant_name, t?.name]) {
    const up = String(raw || '').toUpperCase();
    if (!up) continue;
    for (const [label, re, kind] of KNOWN_SUBSCRIPTION_MERCHANTS) if (re.test(up)) return { label, mixed: kind === 'mixed' };
  }
  if ((t?.personal_finance_category?.detailed || '') === 'PERSONAL_CARE_GYMS_AND_FITNESS_CENTERS') return { label: 'Gym', mixed: true };
  return null;
}
/** The known-subscription label for a charge, or null. */
export const knownSubscriptionMerchant = (t) => knownSubscriptionInfo(t)?.label || null;

// Apple App Store billing: the bank name carries APPLE.COM/BILL (or the
// truncated APPLE.COM/BI); one bill can be several different subscriptions.
export const isAppleBilling = (t) => /APPLE\.COM\/BI/i.test(`${t?.name || ''}`) || /APPLE\.COM\/BILL/i.test(`${t?.merchant_name || ''}`);
const appleKey = (amount) => `APPLE|${(Math.round(Number(amount) * 100) / 100).toFixed(2)}`;
/** A per-amount Apple key "APPLE|1.99" → its base merchant key + amount. */
export const splitKey = (key) => { const m = /^(.*)\|(\d+(?:\.\d+)?)$/.exec(String(key || '')); return m ? { base: m[1], amount: Number(m[2]) } : { base: key, amount: null }; };

// The bank (or Plaid) says this charge recurs (2026-10-22): "RECURRING
// PAYMENT AUTHORIZED ON 10/04 APPLE.COM/BILL" vs "PURCHASE AUTHORIZED ON ...".
// Such a charge is a subscription candidate from its FIRST occurrence.
const RECURRING_NAME_RE = /(?:^|[^A-Z])RECURRING (?:PAYMENT|PYMT|PMT|CHARGE)(?=$|[^A-Z])/;
export const isRecurringFlagged = (t) => t?.is_recurring === true || t?.recurring === true
  || RECURRING_NAME_RE.test(String(t?.name || '').toUpperCase());

const PATTERN_EXCLUDED_PRIMARY = new Set(['FOOD_AND_DRINK', 'GENERAL_MERCHANDISE', 'TRANSPORTATION']);
const DELIVERY_RE = W('DOORDASH|DASHPASS|UBER EATS|UBEREATS|GRUBHUB|INSTACART|POSTMATES|GOPUFF|CAVIAR|SEAMLESS');
const P2P_RE = W('ZELLE|VENMO|CASH APP|SQUARE CASH|PAYPAL|APPLE CASH');
/** (d) + (e): a category or counterparty that is never a subscription by pattern alone. */
export function patternExcluded(t) {
  const primary = spendCategory(t);
  const detailed = t?.personal_finance_category?.detailed || '';
  if (PATTERN_EXCLUDED_PRIMARY.has(primary) || /DELIVERY/.test(detailed)) return true;
  const raw = `${t?.merchant_name || ''} ${t?.name || ''}`.toUpperCase();
  if (DELIVERY_RE.test(raw) || P2P_RE.test(raw)) return true;
  if ((t?.counterparties || []).some(c => c && c.type === 'person')) return true;
  return false;
}

const nearAmount = (a, b, tol) => b > 0 && Math.abs(a - b) <= b * tol;
const DAYS = (a, b) => Math.round((dayMs(b) - dayMs(a)) / DAY);

/**
 * @returns [{ matchName, displayName, amount, lastDate, usualDay, reason: 'known'|'app_store'|'pattern', known }]
 */
export function possibleSubscriptions(transactions = [], { today, zone, bills = [], rules = [], detected = [] } = {}) {
  if (!today) return [];
  const answered = new Set((rules || []).map(r => r.match_name));
  const strict = new Set((detected || []).map(p => p.matchName));
  const strictApple = (detected || []).filter(p => p.matchName === 'APPLE');
  const groups = new Map();
  for (const t of transactions || []) {
    if (!isChargeCandidate(t) || spendBucket(t) !== 'everyday') continue;
    const d = txLocalDay(t, zone); if (!d || d > today) continue;
    const apple = isAppleBilling(t);
    const key = apple ? appleKey(t.amount) : merchantKey(t);
    if (!key) continue;
    if (!groups.has(key)) groups.set(key, { apple, rows: [] });
    const info = apple ? null : knownSubscriptionInfo(t);
    groups.get(key).rows.push({ d, amount: Number(t.amount), name: t.merchant_name || t.name || key, known: info?.label || null, mixed: !!info?.mixed, excluded: patternExcluded(t), recurring: isRecurringFlagged(t) });
  }
  const cutoff = new Date(dayMs(today) - KNOWN_MERCHANT_WINDOW_DAYS * DAY).toISOString().slice(0, 10);
  const out = [];
  for (const [key, { apple, rows }] of groups) {
    if (answered.has(key) || strict.has(key) || (bills || []).some(b => billMatchesKey(b, key))) continue;
    rows.sort((a, b) => a.d.localeCompare(b.d));
    const last = rows[rows.length - 1];
    const gaps = rows.slice(1).map((r, i) => DAYS(rows[i].d, r.d));
    // Trailing run of consecutive charges 25-35 days apart.
    let run = 1; for (let i = gaps.length - 1; i >= 0 && gaps[i] >= 25 && gaps[i] <= 35; i--) run++;
    let reason = null;
    if (apple) {
      // Same amount (the group key), repeating monthly; not one the detector already prompts.
      const dupOfStrict = strictApple.some(p => nearAmount(p.typicalAmount, last.amount, POSSIBLE_AMOUNT_TOLERANCE));
      if (run >= 2 && !dupOfStrict) reason = 'app_store';
      // One "RECURRING PAYMENT" Apple charge at a new amount, in the last 45
      // days, is already an App Store subscription; "PURCHASE" ones stay one-offs.
      else if (!dupOfStrict && rows.some(r => r.recurring && r.d >= cutoff)) reason = 'app_store';
    } else {
      const knownRow = rows.find(r => r.known);
      const known = knownRow?.known || null;
      const onePerCycle = gaps.every(g => g >= 25);
      // Mixed merchant: a same-amount (5%) pair 25-35 days apart.
      const samePair = () => rows.some((a, i) => rows.slice(i + 1).some(b => { const g = DAYS(a.d, b.d); return g >= 25 && g <= 35 && Math.abs(a.amount - b.amount) <= Math.min(a.amount, b.amount) * POSSIBLE_AMOUNT_TOLERANCE; }));
      if (known && !knownRow.mixed && last.d >= cutoff) reason = 'known';
      else if (known && knownRow.mixed && samePair()) reason = 'known';
      // Bank-flagged recurring charge in the last 45 days: a candidate from its
      // first charge (still never a person, P2P, food, merchandise, transport).
      else if (rows.some(r => r.recurring && r.d >= cutoff) && !rows.some(r => r.excluded)) reason = 'recurring';
      else if (onePerCycle && run >= 2 && !rows.some(r => r.excluded)) {
        // (b) within ~5% of EACH OTHER: the largest vs the smallest.
        const amts = rows.slice(rows.length - run).map(r => r.amount);
        if (Math.max(...amts) - Math.min(...amts) <= Math.min(...amts) * POSSIBLE_AMOUNT_TOLERANCE) reason = 'pattern';
      }
    }
    if (!reason) continue;
    out.push({ matchName: key, displayName: apple ? APP_STORE_LABEL : last.name, amount: last.amount, lastDate: last.d,
      usualDay: Number(last.d.slice(8, 10)), reason, known: apple ? 'Apple' : (rows.find(r => r.known)?.known || null) });
  }
  return out.sort((a, b) => b.lastDate.localeCompare(a.lastDate));
}

/** "Add" on a possible subscription: a monthly subscription bill on the last charge's day. */
// `name` (2026-10-20): the user can name it on Add ("App Store subscription"
// → "Crunchyroll"); blank falls back to the detected name.
export function billFromPossible(p, userId, name = null) {
  const n = String(name || '').trim() || p.displayName;
  return billFromManual({ name: n, amount: p.amount, day: p.usualDay, cadence: 'monthly', matchName: p.matchName }, userId);
}

// ── HIDDEN (2026-10-20) ───────────────────────────────────────────────────
// Merchants answered "not a bill" / "Not a subscription", with their last known
// amount and day (the rule's own, else the latest charge), for Restore.
export function hiddenSubscriptions(rules = [], transactions = [], { zone, bills = [] } = {}) {
  const latest = new Map();
  for (const t of transactions || []) {
    if (!isChargeCandidate(t)) continue;
    const key = merchantKey(t); const d = txLocalDay(t, zone);
    if (!key || !d) continue;
    const cur = latest.get(key);
    if (!cur || d > cur.d) latest.set(key, { d, amount: Number(t.amount), name: t.merchant_name || t.name || key });
  }
  // A merchant that is a bill again (re-added by hand) is not hidden.
  return (rules || []).filter(r => r.decision === 'not_bill' && r.match_name && !(bills || []).some(b => billMatchesKey(b, r.match_name))).map(r => {
    const { base, amount: keyAmount } = splitKey(r.match_name);
    const l = keyAmount != null ? null : latest.get(base);
    const amount = r.typical_amount != null ? Number(r.typical_amount) : keyAmount != null ? keyAmount : (l ? l.amount : 0);
    const day = r.usual_day != null ? Number(r.usual_day) : (l ? Number(l.d.slice(8, 10)) : 1);
    const name = keyAmount != null && base === 'APPLE' ? APP_STORE_LABEL : l ? l.name : String(base).toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    // dismissedFrom 'possible' → Restore puts it back in Possible (no bill).
    const row = { matchName: r.match_name, displayName: name, amount, usualDay: day, dismissedFrom: r.dismissed_from || null };
    // Shown as Restore will bring it back: from real charges when there are any.
    const plan = restorePlan(row, transactions, { zone });
    return plan.fromCharges && plan.cadence === 'monthly' ? { ...row, amount: plan.amount, usualDay: plan.day } : row;
  });
}

/**
 * The not_bill answer "Not a subscription" on a Possible row saves: keyed by
 * the row's matchName (Apple billing is per amount, "APPLE|18.98", so one
 * Apple amount is dismissed without hiding the others).
 */
export function dismissPossibleRule(p, userId) {
  return { user_id: userId, match_name: p.matchName, decision: 'not_bill', typical_amount: p.amount, usual_day: p.usualDay, dismissed_from: 'possible' };
}
/** Does Restore re-add a bill (true) or just return the row to Possible (false)? */
export const restoreAddsBill = (h) => h?.dismissedFrom !== 'possible';

// ── RESTORE FROM REAL CHARGES (2026-10-21) ─────────────────────────────────
/** The user's charges for an answer key: a merchant key, or "APPLE|<amount>" (Apple billing at that amount, within 5%). */
export function chargesForKey(key, transactions = [], { zone } = {}) {
  const { base, amount } = splitKey(key);
  return (transactions || []).filter(t => isChargeCandidate(t) && txLocalDay(t, zone)
    && (amount != null ? (isAppleBilling(t) && nearAmount(Number(t.amount), amount, POSSIBLE_AMOUNT_TOLERANCE)) : merchantKey(t) === base));
}

/**
 * What a hidden answer restores as, recomputed from the user's matching
 * charges with the detector's own logic (amount, cadence, usual day, weekday)
 * and linked to the merchant (match_name = the key). With 1-2 charges (no
 * run yet): monthly, the latest amount, the median day. Only with NO matching
 * charges does it fall back to the stored answer's amount/day.
 * @returns {{ name, amount, cadence, day, month: null, matchName, fromCharges: boolean }}
 */
export function restorePlan(h, transactions = [], { zone } = {}) {
  const charges = chargesForKey(h.matchName, transactions, { zone });
  const apple = splitKey(h.matchName).amount != null;
  if (charges.length) {
    // Apple: detect within that amount's charges only (merchant key APPLE groups every amount).
    const [p] = detectRecurringCharges(charges, { zone });
    const rows = charges.map(t => ({ d: txLocalDay(t, zone), amount: Number(t.amount), name: t.merchant_name || t.name })).sort((a, b) => a.d.localeCompare(b.d));
    const last = rows[rows.length - 1];
    const name = apple ? (h.displayName || APP_STORE_LABEL) : last.name;
    if (p) {
      return { name, amount: p.typicalAmount, cadence: p.cadence, day: p.cadence === 'weekly' ? p.weekday : p.usualDay, month: null, matchName: h.matchName, fromCharges: true };
    }
    return { name, amount: last.amount, cadence: 'monthly', day: Math.min(28, Math.round(median(rows.map(r => Number(r.d.slice(8, 10)))))), month: null, matchName: h.matchName, fromCharges: true };
  }
  return { name: h.displayName, amount: h.amount, cadence: 'monthly', day: h.usualDay, month: null, matchName: h.matchName, fromCharges: false };
}

/** Restore: the subscription bill a hidden merchant comes back as (from real charges when there are any). */
export function billFromHidden(h, userId, transactions = [], { zone } = {}) {
  return billFromManual(restorePlan(h, transactions, { zone }), userId);
}

/**
 * The not_bill answer removing a subscription stores, or null (2026-10-21):
 * only a detected/linked bill (match_name) has anything to block; a
 * hand-entered bill that was never linked stores nothing.
 */
export function removalRuleFor(b, userId) {
  if (!b?.match_name) return null;
  return { user_id: userId, match_name: b.match_name, decision: 'not_bill', typical_amount: Number(b.amount) || null, usual_day: b.due_day ?? null, dismissed_from: 'subscriptions' };
}

/**
 * Manual add suggestions: merchants from the last `days` (90) of charges whose
 * name contains what was typed, with their last amount and usual day of month
 * (median), newest first. Empty query → none.
 */
export function suggestMerchants(transactions = [], query = '', { zone, today, days = 90, limit = 5 } = {}) {
  const q = String(query || '').trim().toUpperCase();
  if (q.length < 2 || !today) return [];
  const cutoff = new Date(dayMs(today) - days * DAY).toISOString().slice(0, 10);
  const groups = new Map();
  for (const t of transactions || []) {
    if (!isChargeCandidate(t)) continue;
    const d = txLocalDay(t, zone); const key = merchantKey(t);
    if (!key || !d || d < cutoff || d > today) continue;
    const name = t.merchant_name || t.name || key;
    if (!key.includes(q.replace(/[^A-Z ]/g, ' ').replace(/\s+/g, ' ').trim()) && !name.toUpperCase().includes(q)) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ d, amount: Number(t.amount), name });
  }
  return [...groups.entries()].map(([key, rows]) => {
    rows.sort((a, b) => a.d.localeCompare(b.d));
    const last = rows[rows.length - 1];
    return { matchName: key, displayName: last.name, amount: last.amount, lastDate: last.d, usualDay: Math.round(median(rows.map(r => Number(r.d.slice(8, 10))))) };
  }).sort((a, b) => b.lastDate.localeCompare(a.lastDate)).slice(0, limit);
}
